import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Award, Edit, FileDown, Printer, X, Search, Save, Image as ImageIcon, Type } from 'lucide-react';
import BadgeIcon from './BadgeIcon';
import { fmtDateNo } from '../utils/dateFormat';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fetchDiplomaBlob = async (awardId, opts) => {
  const params = new URLSearchParams();
  if (opts) {
    if (opts.achieved_at) params.set('achieved_at', opts.achieved_at);
    params.set('logo_mode', opts.logo_mode);
    params.set('show_footer', String(opts.show_footer));
  }
  const qs = params.toString();
  const r = await axios.get(`${API_BASE}/api/merkeskyting/diploma/${awardId}${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(), responseType: 'blob',
  });
  return new Blob([r.data], { type: 'application/pdf' });
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const printBlob = (blob) => {
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  iframe.src = url;
  iframe.onload = () => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
    catch { window.open(url, '_blank'); }
    setTimeout(() => { iframe.remove(); URL.revokeObjectURL(url); }, 60000);
  };
  document.body.appendChild(iframe);
};

const fileName = (a) => `diplom_${a.level}_${(a.member_name || 'medlem').replace(/ /g, '_')}.pdf`;

const Toggle = ({ checked, onChange, testId }) => (
  <button type="button" onClick={() => onChange(!checked)} data-testid={testId}
    className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-yellow-500' : 'bg-gray-600'}`}>
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${checked ? 'translate-x-5' : ''}`} />
  </button>
);

const LogoModePicker = ({ value, onChange, prefix }) => (
  <div className="grid grid-cols-2 gap-2">
    {[{ k: 'text', label: 'Klubbnavn i tekst', Icon: Type }, { k: 'logo', label: 'Klubbens logo', Icon: ImageIcon }].map(({ k, label, Icon }) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${prefix}-logo-mode-${k}`}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${value === k ? 'border-yellow-500 bg-yellow-500/15 text-yellow-200' : 'border-gray-600 bg-gray-700/50 text-gray-300 hover:bg-gray-700'}`}>
        <Icon className="w-4 h-4" /> {label}
      </button>
    ))}
  </div>
);

const DefaultsCard = ({ defaults, onSaved }) => {
  const toast = useToast();
  const [logoMode, setLogoMode] = useState(defaults.logo_mode);
  const [showFooter, setShowFooter] = useState(defaults.show_footer);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/merkeskyting/settings/diploma-defaults`,
        { logo_mode: logoMode, show_footer: showFooter }, { headers: authHeaders() });
      onSaved({ logo_mode: logoMode, show_footer: showFooter });
      toast.success('Standard for diplomer lagret');
    } catch { toast.error('Kunne ikke lagre standard'); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-3 rounded-lg bg-gray-700/40 border border-gray-700 space-y-3" data-testid="diploma-defaults-card">
      <p className="text-white text-sm font-semibold">Klubbens standard for diplomer</p>
      <p className="text-gray-400 text-xs">Brukes på alle diplomer som ikke har egne valg.</p>
      <LogoModePicker value={logoMode} onChange={setLogoMode} prefix="defaults" />
      <div className="flex items-center justify-between">
        <span className="text-gray-300 text-sm">Vis «Aktivlogg – digital protokoll for merkeskyting»</span>
        <Toggle checked={showFooter} onChange={setShowFooter} testId="defaults-footer-toggle" />
      </div>
      <button onClick={save} disabled={saving} data-testid="defaults-save-btn"
        className="inline-flex items-center gap-2 px-4 py-1.5 bg-yellow-500 text-gray-900 rounded text-sm font-medium hover:bg-yellow-400 disabled:bg-gray-600">
        <Save className="w-4 h-4" /> {saving ? 'Lagrer...' : 'Lagre standard'}
      </button>
    </div>
  );
};

const EditDiplomaModal = ({ award, defaults, onClose, onSaved }) => {
  const toast = useToast();
  const [achievedAt, setAchievedAt] = useState(String(award.achieved_at || '').slice(0, 10));
  const [logoMode, setLogoMode] = useState(award.diploma_logo_mode || defaults.logo_mode);
  const [showFooter, setShowFooter] = useState(award.diploma_show_footer ?? defaults.show_footer);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const opts = useMemo(() => ({ achieved_at: achievedAt, logo_mode: logoMode, show_footer: showFooter }), [achievedAt, logoMode, showFooter]);

  useEffect(() => {
    let url; let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const blob = await fetchDiplomaBlob(award.id, opts);
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } catch { if (!cancelled) toast.error('Kunne ikke lage forhåndsvisning'); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line
  }, [opts, award.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const persist = async () => {
    const r = await axios.put(`${API_BASE}/api/merkeskyting/awards/${award.id}/diploma`, opts, { headers: authHeaders() });
    onSaved(r.data.award);
  };

  const run = async (action) => {
    setBusy(true);
    try {
      await persist();
      if (action === 'save') { toast.success('Diplom lagret'); onClose(); return; }
      const blob = await fetchDiplomaBlob(award.id);
      if (action === 'download') downloadBlob(blob, fileName(award)); else printBlob(blob);
      toast.success(action === 'download' ? 'Diplom lagret og lastet ned' : 'Diplom lagret – åpner utskrift');
    } catch (e) { toast.error(e.response?.data?.detail || 'Noe gikk galt'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 md:p-4" onClick={onClose}>
      <div className="bg-gray-900 rounded-xl w-full max-w-5xl max-h-[95vh] overflow-y-auto border border-gray-700" onClick={(e) => e.stopPropagation()} data-testid="edit-diploma-modal">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-4 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-3 min-w-0">
            <BadgeIcon level={award.level} size={32} />
            <div className="min-w-0">
              <p className="text-white font-bold truncate">Rediger diplom – {award.member_name}</p>
              <p className="text-gray-400 text-xs truncate">{award.level_name} · {award.discipline_name} – {award.variant_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="edit-diploma-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Dato for oppnådd merke</label>
              <input type="date" value={achievedAt} onChange={(e) => setAchievedAt(e.target.value)} data-testid="edit-diploma-date"
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white" />
              <p className="text-gray-500 text-xs mt-1">Endrer kun datoen på diplomet og i oversikten – resultatene påvirkes ikke.</p>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Klubb-visning på diplomet</label>
              <LogoModePicker value={logoMode} onChange={setLogoMode} prefix="edit-diploma" />
              <p className="text-gray-500 text-xs mt-1">Logo plasseres midt på der klubbnavnet står. Krever at klubben har lastet opp logo.</p>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-300 text-sm">Vis «Aktivlogg – digital protokoll for merkeskyting»</span>
              <Toggle checked={showFooter} onChange={setShowFooter} testId="edit-diploma-footer-toggle" />
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <button onClick={() => run('save')} disabled={busy} data-testid="edit-diploma-save-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 disabled:bg-gray-600">
                <Save className="w-4 h-4" /> Lagre
              </button>
              <button onClick={() => run('download')} disabled={busy} data-testid="edit-diploma-download-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50">
                <FileDown className="w-4 h-4" /> Lagre og last ned PDF
              </button>
              <button onClick={() => run('print')} disabled={busy} data-testid="edit-diploma-print-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50">
                <Printer className="w-4 h-4" /> Lagre og skriv ut
              </button>
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg min-h-[420px] lg:min-h-[640px] flex items-center justify-center overflow-hidden">
            {previewUrl
              ? <iframe title="Forhåndsvisning av diplom" src={`${previewUrl}#toolbar=0&view=FitH`} className="w-full h-[420px] lg:h-[640px] bg-white" data-testid="edit-diploma-preview" />
              : <p className="text-gray-400 text-sm">Lager forhåndsvisning...</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

const DiplomaProgress = ({ p, id }) => {
  const count = Math.min(10, p.count || 0);
  return (
    <div className="mt-1.5" data-testid={`diploma-progress-${id}`}>
      <p className="text-[11px] text-gray-300 truncate">
        {p.completed ? (
          <span className="text-yellow-300 font-medium">Alle nivåer fullført</span>
        ) : (
          <>Neste: <strong className="text-white">{p.next_level_name}</strong> – {count}/10 serier{p.threshold != null && <span className="text-gray-400"> (krav {p.threshold} {p.unit_label === 'poeng' || !p.unit_label ? 'p' : p.unit_label})</span>}</>
        )}
      </p>
      <div className="h-1.5 mt-1 rounded-full bg-gray-600/60 overflow-hidden">
        <div className={`h-full rounded-full transition-[width] duration-500 ${p.completed ? 'bg-yellow-400' : 'bg-green-500'}`}
          style={{ width: `${p.completed ? 100 : count * 10}%` }} data-testid={`diploma-progress-bar-${id}`} />
      </div>
    </div>
  );
};

const DiplomaRow = ({ a, defaults, canEdit, onEdit, onQuick }) => {
  const logoMode = a.diploma_logo_mode || defaults.logo_mode;
  const footer = a.diploma_show_footer ?? defaults.show_footer;
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-700/40 border border-gray-700 hover:border-gray-600 transition-colors" data-testid={`diploma-row-${a.id}`}>
      <BadgeIcon level={a.level} size={36} />
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold truncate">{a.member_name || 'Ukjent medlem'}</p>
        <p className="text-yellow-300 text-xs truncate">{a.level_name} · {a.discipline_name} – {a.variant_name}</p>
        <p className="text-gray-400 text-xs" data-testid={`diploma-date-${a.id}`}>
          Oppnådd: {fmtDateNo(a.achieved_at)}{a.achieved_at_manual && <span className="ml-1 text-orange-300">(manuelt endret)</span>}
          <span className="text-gray-500"> · {logoMode === 'logo' ? 'Logo' : 'Klubbnavn'}{footer ? '' : ' · uten Aktivlogg-linje'}</span>
        </p>
        {a.progress && <DiplomaProgress p={a.progress} id={a.id} />}
      </div>
      <div className="flex items-center gap-1">
        {canEdit && (
          <button onClick={() => onEdit(a)} title="Rediger diplom" className="p-2 text-yellow-400 hover:bg-gray-700 rounded" data-testid={`diploma-edit-${a.id}`}><Edit className="w-4 h-4" /></button>
        )}
        <button onClick={() => onQuick(a, 'download')} title="Last ned PDF" className="p-2 text-gray-300 hover:bg-gray-700 rounded" data-testid={`diploma-download-${a.id}`}><FileDown className="w-4 h-4" /></button>
        <button onClick={() => onQuick(a, 'print')} title="Skriv ut" className="p-2 text-gray-300 hover:bg-gray-700 rounded" data-testid={`diploma-print-${a.id}`}><Printer className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const DiplomaManager = ({ settings, canEdit }) => {
  const toast = useToast();
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState('');
  const [year, setYear] = useState('');
  const [editing, setEditing] = useState(null);
  const [defaults, setDefaults] = useState({
    logo_mode: settings?.diploma_default_logo_mode || 'text',
    show_footer: settings?.diploma_default_show_footer ?? true,
  });

  useEffect(() => {
    axios.get(`${API_BASE}/api/merkeskyting/awards?include_progress=true`, { headers: authHeaders() })
      .then(r => setAwards((r.data.awards || []).filter(a => a.status !== 'reopened')))
      .catch(() => toast.error('Kunne ikke hente diplomer'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const years = useMemo(() => Array.from(new Set(awards.map(a => String(a.achieved_at || '').slice(0, 4)).filter(Boolean))).sort().reverse(), [awards]);
  const filtered = useMemo(() => awards.filter(a =>
    (!search || (a.member_name || '').toLowerCase().includes(search.toLowerCase())) &&
    (!level || a.level === level) &&
    (!year || String(a.achieved_at || '').startsWith(year))
  ).sort((a, b) => (a.member_name || '').localeCompare(b.member_name || '', 'no', { sensitivity: 'base' })), [awards, search, level, year]);

  const quick = async (a, action) => {
    try {
      const blob = await fetchDiplomaBlob(a.id);
      if (action === 'download') downloadBlob(blob, fileName(a)); else printBlob(blob);
    } catch { toast.error('Kunne ikke hente diplom'); }
  };

  const onSaved = (updated) => setAwards(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x));

  return (
    <div className="bg-gray-800 rounded-lg p-4 space-y-3" data-testid="diploma-manager">
      <div className="flex items-center gap-2">
        <Award className="w-5 h-5 text-yellow-400" />
        <h3 className="text-white font-bold">Oppnådde diplomer</h3>
        <span className="ml-auto text-xs text-gray-400" data-testid="diploma-count">{filtered.length} diplomer</span>
      </div>
      {canEdit && <DefaultsCard defaults={defaults} onSaved={setDefaults} />}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-500 absolute left-2.5 top-2.5" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Søk medlem..." data-testid="diploma-search"
            className="w-full bg-gray-700 border border-gray-600 rounded pl-8 pr-3 py-2 text-white text-sm" />
        </div>
        <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="diploma-filter-level" className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm">
          <option value="">Alle nivåer</option>
          {['nybegynner', 'viderekommende', 'bronse', 'solv', 'gull'].map(l => <option key={l} value={l}>{{ nybegynner: 'Nybegynner', viderekommende: 'Viderekommende', bronse: 'Bronse', solv: 'Sølv', gull: 'Gull' }[l]}</option>)}
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="diploma-filter-year" className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm">
          <option value="">Alle år</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {loading ? <p className="text-gray-400 text-sm">Laster diplomer...</p> : filtered.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-4" data-testid="diploma-empty">Ingen diplomer funnet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[480px] overflow-y-auto pr-1">
          {filtered.map(a => <DiplomaRow key={a.id} a={a} defaults={defaults} canEdit={canEdit} onEdit={setEditing} onQuick={quick} />)}
        </div>
      )}
      {editing && <EditDiplomaModal award={editing} defaults={defaults} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
};

export default DiplomaManager;
